import { useMemo } from "react";
import {
  Link,
  createRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";
import {
  usePublicGatherings,
  type PublicGatheringSummary,
} from "@/lib/discover/queries";
import { WaitlistForm } from "@/components/marketing/WaitlistForm";
import { rootRoute } from "./root";
import styles from "./discover.module.scss";

// Sentinel value used in the dropdown to mean "Other city — show waitlist".
const OTHER_CITY = "__other__";

const searchSchema = z.object({
  city: z.string().optional(),
});

type DiscoverSearch = z.infer<typeof searchSchema>;

export function DiscoverRoute() {
  const search = useSearch({ from: discoverRoute.id }) as DiscoverSearch;
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePublicGatherings();

  const gatherings = useMemo(() => data ?? [], [data]);

  // Cities derived from the API response — DISTINCT, sorted alphabetically.
  const cities = useMemo(() => {
    const set = new Set<string>();
    for (const g of gatherings) {
      if (g.city) set.add(g.city);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [gatherings]);

  // Selected city: URL ?city=, falling back to the first city with gatherings.
  // If the URL value is the OTHER sentinel, keep it; otherwise treat any
  // mismatch as "unlisted" (still empty state, with the user-typed value
  // pre-filled in the waitlist form).
  const cityParam = search.city ?? "";
  const selectedCity = cityParam === "" ? (cities[0] ?? OTHER_CITY) : cityParam;

  const isOther = selectedCity === OTHER_CITY;
  const filtered = useMemo(() => {
    if (isOther) return [] as PublicGatheringSummary[];
    return gatherings
      .filter((g) => g.city === selectedCity)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [gatherings, selectedCity, isOther]);

  function setCity(value: string): void {
    navigate({
      to: "/discover",
      search: value === "" ? {} : { city: value },
      replace: true,
    });
  }

  return (
    <main className={styles.root}>
      <header className={styles.topbar}>
        <Link to="/" className={styles.brand}>
          Plotlounge
        </Link>
        <div className={styles.cityPicker}>
          <label className={styles.pickerLabel} htmlFor="discover-city">
            Showing
          </label>
          <select
            id="discover-city"
            aria-label="City"
            className={styles.pickerSelect}
            value={selectedCity}
            onChange={(e) => setCity(e.target.value)}
          >
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            <option value={OTHER_CITY}>Other city…</option>
          </select>
        </div>
        <Link to="/sign-in" className={styles.signin}>
          Sign in
        </Link>
      </header>

      <section className={styles.feed}>
        {isLoading ? (
          <p className={styles.status} role="status" aria-live="polite">
            Loading gatherings…
          </p>
        ) : isError ? (
          <p className={styles.status} role="alert">
            Couldn't load gatherings. Please refresh.
          </p>
        ) : filtered.length === 0 ? (
          <EmptyState
            city={isOther ? "" : selectedCity}
            // Re-mount the form when the city target changes so the input
            // re-initializes from defaultCity.
            key={selectedCity}
          />
        ) : (
          <DateGroupedFeed gatherings={filtered} />
        )}
      </section>
    </main>
  );
}

function DateGroupedFeed({
  gatherings,
}: {
  gatherings: PublicGatheringSummary[];
}) {
  const grouped = useMemo(() => groupByBucket(gatherings), [gatherings]);

  return (
    <div className={styles.feedInner}>
      {grouped.map(([bucket, items]) => (
        <div key={bucket} className={styles.bucket}>
          <header className={styles.bucketHead}>
            <h2 className={styles.bucketTitle}>{bucket}</h2>
            <span className={styles.bucketCount}>
              {items.length} {items.length === 1 ? "gathering" : "gatherings"}
            </span>
          </header>
          <ol className={styles.bucketList}>
            {items.map((g) => (
              <GatheringRow key={g.id} gathering={g} />
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

function GatheringRow({ gathering }: { gathering: PublicGatheringSummary }) {
  const remaining =
    gathering.capacity != null
      ? Math.max(0, gathering.capacity - gathering.rsvpCount)
      : null;
  const dateLabel = formatLongDate(gathering.date);
  const timeLabel = formatTime(gathering.date);

  return (
    <li className={styles.entry}>
      <a href={`/g/${gathering.id}`} className={styles.entryLink}>
        <div className={styles.dateCol}>
          <span className={styles.dateDay}>{dateLabel}</span>
          <span className={styles.dateTime}>{timeLabel}</span>
        </div>
        <div className={styles.contentCol}>
          <p className={styles.entryClub}>
            {gathering.club.name}
            {gathering.neighborhood ? ` · ${gathering.neighborhood}` : ""}
          </p>
          <h3 className={styles.entryTitle}>{gathering.title}</h3>
        </div>
        <div className={styles.metaCol}>
          {remaining != null ? (
            <>
              <span className={styles.seats}>{remaining} seats</span>
              <span className={styles.seatsTotal}>of {gathering.capacity}</span>
            </>
          ) : (
            <span className={styles.seatsTotal}>Open</span>
          )}
          <span className={styles.cta}>RSVP →</span>
        </div>
      </a>
    </li>
  );
}

function EmptyState({ city }: { city: string }) {
  const heading = city ? `Nothing in ${city} yet.` : "Tell us where you read.";
  const lede = city
    ? "We're growing slowly. Drop your email and we'll write when we open in your city."
    : "We open in a new city when enough readers ask. Tell us where you are.";

  return (
    <div className={styles.empty}>
      <h3 className={styles.emptyTitle}>{heading}</h3>
      <p className={styles.emptyLede}>{lede}</p>
      <WaitlistForm
        source="discover-city"
        defaultCity={city}
        cityLabel="city"
        successMessage="Thanks. We'll write when a club opens in your city."
        submitLabel="Notify me"
      />
    </div>
  );
}

// ─── Date bucketing ──────────────────────────────────────────────────────────

const BUCKETS = [
  "Today",
  "This week",
  "Next week",
  "Later this month",
  "Coming up",
] as const;
type Bucket = (typeof BUCKETS)[number];

function bucketLabel(iso: string, now: Date): Bucket {
  const start = new Date(iso);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((start.getTime() - now.getTime()) / dayMs);
  if (diffDays <= 0) return "Today";
  if (diffDays <= 7) return "This week";
  if (diffDays <= 14) return "Next week";
  if (diffDays <= 30) return "Later this month";
  return "Coming up";
}

function groupByBucket(
  gatherings: PublicGatheringSummary[],
): Array<[Bucket, PublicGatheringSummary[]]> {
  const now = new Date();
  const map = new Map<Bucket, PublicGatheringSummary[]>();
  for (const g of gatherings) {
    const key = bucketLabel(g.date, now);
    const arr = map.get(key) ?? [];
    arr.push(g);
    map.set(key, arr);
  }
  // Preserve canonical bucket order.
  return BUCKETS.filter((b) => map.has(b)).map((b) => [b, map.get(b)!]);
}

function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export const discoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/discover",
  validateSearch: searchSchema,
  component: DiscoverRoute,
});
